import { OrderParty, OrderFormat, OrderStatus, BillTo, LabPriority, CollectionDate, CollectionTime, Fasting, InstructionType, OfficeType } from '@caresend/types';
import update from 'immutability-helper';
import { initBooking } from './booking.js';
import { initTimeZoneDate } from './date.js';
import { initElementTimestamp } from './general.js';
import { initHealthGorillaInfo } from './healthGorilla.js';
import { generateID } from './id.js';
import { initProcedure } from './procedure.js';

const initOrderFlowSettings = () => ({
  orderingParty: OrderParty.OFFICE,
  schedulingParty: OrderParty.PATIENT,
  orderingFormat: OrderFormat.MANUAL
});
const initOrderFlowData = () => ({
  flowSettings: initOrderFlowSettings()
});
const initDraftOrder = (id,
/**
 * Patient might not be logged in yet when starting a draft order.
 */
createdBy, officeID) => Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, createdBy ? {
  createdBy
} : undefined), officeID ? {
  createdByOfficeID: officeID
} : undefined), {
  creationTimestamp: Date.now(),
  flowData: initOrderFlowData(),
  id,
  isFulfilledByOffice: false
}), officeID ? {
  officeID
} : undefined), {
  status: OrderStatus.DRAFT
});
const initDraftProcedure = (bookingID, newTaskTypeID, draftOrderID, labSettings, draftBooking, isOfficeLab) => {
  var _a, _b, _c, _d, _e;

  const procedure = initProcedure(bookingID, draftOrderID, newTaskTypeID);
  procedure.billTo = (_a = labSettings === null || labSettings === void 0 ? void 0 : labSettings.selectedBillingOption) !== null && _a !== void 0 ? _a : BillTo.INSURANCE;
  procedure.labPriority = (_b = labSettings === null || labSettings === void 0 ? void 0 : labSettings.selectedLabProcessingSpeedOption) !== null && _b !== void 0 ? _b : LabPriority.ROUTINE;
  procedure.collectionDate = (_c = labSettings === null || labSettings === void 0 ? void 0 : labSettings.selectedCollectionDateOption) !== null && _c !== void 0 ? _c : CollectionDate.ANY;
  procedure.collectionTime = (_d = labSettings === null || labSettings === void 0 ? void 0 : labSettings.selectedCollectionTimeOption) !== null && _d !== void 0 ? _d : CollectionTime.ANY;

  if (draftBooking === null || draftBooking === void 0 ? void 0 : draftBooking.documents) {
    /**
     * Type cast since booking documents will always be of this type in this order flow
     * Potential TODO: remove type cast and write a type validation function
     */
    procedure.documents = draftBooking.documents;
  }

  const defaultFasting = (_e = labSettings === null || labSettings === void 0 ? void 0 : labSettings.selectedFastingOption) !== null && _e !== void 0 ? _e : Fasting.NOT_REQUIRED;

  if (defaultFasting === Fasting.NOT_REQUIRED) {
    procedure.instructions = [{
      type: InstructionType.FASTING,
      instruction: 'No'
    }];
  } else {
    procedure.instructions = [{
      type: InstructionType.FASTING,
      instruction: 'Yes'
    }];
  }

  if (isOfficeLab) {
    if (draftBooking === null || draftBooking === void 0 ? void 0 : draftBooking.labOrderID) {
      procedure.healthGorillaInfo = initHealthGorillaInfo(draftBooking.labOrderID);
    }
  }

  return procedure;
};
const initPatientInitiatedDraftOrder = async (taskTypeID, office, location) => {
  var _a, _b, _c, _d;

  const draftOrderID = generateID();
  const labSettings = (_a = office === null || office === void 0 ? void 0 : office.orderSettings) === null || _a === void 0 ? void 0 : _a.labSettings;
  let draftOrder = Object.assign(Object.assign({}, initDraftOrder(draftOrderID, undefined, office === null || office === void 0 ? void 0 : office.id)), {
    officeID: (_b = office === null || office === void 0 ? void 0 : office.id) !== null && _b !== void 0 ? _b : ''
  });
  let draftBookings = {};
  const booking = initBooking(draftOrderID);
  draftBookings = update(draftBookings, {
    [booking.id]: {
      $set: booking
    }
  });
  draftOrder = update(draftOrder, {
    bookings: {
      $set: update((_c = draftOrder.bookings) !== null && _c !== void 0 ? _c : {}, {
        [booking.id]: {
          $set: initElementTimestamp(booking.id)
        }
      })
    }
  });
  let draftProcedures = {};
  const procedure = initDraftProcedure(booking.id, taskTypeID, draftOrder.id, labSettings, booking, (office === null || office === void 0 ? void 0 : office.type) === OfficeType.LAB);

  if (location) {
    procedure.location = location;
  }

  draftProcedures = update(draftProcedures, {
    [procedure.id]: {
      $set: procedure
    }
  });
  const procedures = {
    [procedure.id]: initElementTimestamp(procedure.id)
  };
  draftBookings = update(draftBookings, {
    [booking.id]: {
      procedures: {
        $set: procedures
      }
    }
  });
  const draftWaypoints = {};
  return {
    data: (_d = draftOrder === null || draftOrder === void 0 ? void 0 : draftOrder.flowData) !== null && _d !== void 0 ? _d : initOrderFlowData(),
    draftBookings,
    draftOrder,
    draftProcedures,
    draftWaypoints,
    id: draftOrderID,
    lastUpdatedDate: initTimeZoneDate(),
    readyForScheduling: true
  };
};

export { initDraftOrder, initDraftProcedure, initOrderFlowData, initOrderFlowSettings, initPatientInitiatedDraftOrder };
